import type { Translate } from 'next-translate';

import type { PublicGame } from '~/domains/contest/domains/services/interfaces/propsService/api/game';
import type { PublicTeam } from '~/domains/contest/domains/services/interfaces/propsService/api/team';
import { formatDollarAmount, formatNumericPrice } from '~/domains/market/utils/pricing';
import type { PriceDisplayFormat } from '~/domains/market/state/marketSettings.store';

import type { PublicPosition } from '../services/interfaces/positions.types';
import type { Position } from '../types/positions.types';
import { getPositionDisplay, resolvePositionEntities } from '../utils/positionDisplay';
import {
  getPositionCurrentPrice,
  getPositionPayout,
  getPositionPnl,
  getPositionTrend,
} from '../utils/positionMetrics';

export function transformPosition(
  publicPosition: PublicPosition,
  eventsById: Map<string, PublicGame>,
  teamsById: Map<string, PublicTeam>,
  priceFormat: PriceDisplayFormat,
  t: Translate
): Position | null {
  const display = getPositionDisplay(
    publicPosition,
    resolvePositionEntities(publicPosition, eventsById, teamsById),
    t
  );
  if (!display) return null;

  const isClosed = publicPosition.status === 'closed';

  const currentValue = publicPosition.quantity * (getPositionCurrentPrice(publicPosition) ?? 0);
  const costBasis = isClosed
    ? publicPosition.closed_cost_plus_fees
    : publicPosition.cost_plus_fees;
  const entryPrice = isClosed ? (publicPosition.closed_price ?? 0) : publicPosition.price;
  const profitLoss = getPositionPnl(publicPosition);

  return {
    id: publicPosition.id,
    market: display.title,
    teamName: display.teamName,
    teamIconUrl: display.teamIconUrl,
    marketType: display.marketType,
    league: display.league,
    gameInfo: display.subtitle,
    cost: formatDollarAmount(costBasis),
    orderCount: publicPosition.order_count > 1 ? publicPosition.order_count : undefined,
    price: formatNumericPrice(entryPrice, priceFormat),
    payoutIfRight: formatDollarAmount(publicPosition.quantity),
    currentValue: formatDollarAmount(currentValue),
    soldFor: formatDollarAmount(getPositionPayout(publicPosition)),
    profitLoss: `${profitLoss >= 0 ? '+' : '-'}${formatDollarAmount(Math.abs(profitLoss))}`,
    trend: getPositionTrend(publicPosition),
  };
}

export function transformPositions(
  positions: PublicPosition[],
  eventsById: Map<string, PublicGame>,
  teamsById: Map<string, PublicTeam>,
  priceFormat: PriceDisplayFormat,
  t: Translate
): Position[] {
  return positions
    .map((pos) => transformPosition(pos, eventsById, teamsById, priceFormat, t))
    .filter((pos): pos is Position => pos !== null);
}
